import React from "react";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import RBDropdown, {
  DropdownProps as RBDropdownProps,
} from "react-bootstrap/Dropdown";
import RBDropdownItem, {
  DropdownItemProps as RBDropdownItemProps,
} from "react-bootstrap/DropdownItem";
import RBDropdownMenu, {
  DropdownMenuProps,
} from "react-bootstrap/DropdownMenu";
import styled from "styled-components/macro";

import Button, { UIButtonProps } from "ui/Button";
import { theme } from "ui/theme";

import { ActionType } from "utils/analytics/analyticsConstants";
import { useAnalytics } from "utils/analytics/AnalyticsContext";
import { AnalyticsProps } from "utils/analytics/AnalyticsProps";

const $DropdownMenu = styled(RBDropdownMenu)`
  border-radius: 0.5rem;
`;

export type { DropdownMenuProps };

interface DropdownItemProps extends RBDropdownItemProps, AnalyticsProps {
  eventType?: ActionType;
}

const $DropdownItem = styled(RBDropdownItem)`
  color: ${theme.ColorSpace500};

  &:focus,
  &:hover {
    background-color: ${theme.ColorSneaker50};
    color: ${theme.ColorBlurple500};
  }

  &:active,
  &.active {
    background-color: ${theme.ColorBlurple500};
    color: ${theme.ColorWhite};
  }
`;

function DropdownItem({
  analyticsName,
  analyticsProperties,
  analyticsSurface,
  eventType,
  ...props
}: DropdownItemProps) {
  const { trackEvent } = useAnalytics(`DropdownItem ${analyticsName}`);
  return (
    <$DropdownItem
      {...props}
      onClick={(e: React.MouseEvent<HTMLElement>) => {
        trackEvent({
          eventName: analyticsName,
          eventType: eventType || ActionType.CLICK,
          analyticsSurface,
          extraProperties: analyticsProperties,
        });

        props.onClick && props.onClick(e);
      }}
    />
  );
}

export interface DropdownToggleProps extends Omit<UIButtonProps, "as"> {
  as?: React.ElementType;
  id: string; // Required for accessibility.
  hideCaret?: boolean;
}

const $DropdownToggle = styled(RBDropdown.Toggle)`
  // Control caret behavior.
  &::before,
  &::after,
  .dropup &::before,
  .dropup &::after {
    ${(props) => props.$hideCaret && "display: none;"}
  }
`;

/**
 * `DropdownToggle` that uses Along's Button component as the default.
 */
function DropdownToggle({ as, hideCaret, ...props }: DropdownToggleProps) {
  return (
    <$DropdownToggle
      forwardedAs={as || Button}
      $hideCaret={hideCaret || props.variant === "icon"}
      {...props}
    />
  );
}

export interface DropdownProps extends RBDropdownProps {
  /**
   * If `true`, the component will use `ButtonGroup` as the container element
   * to group multiple buttons. This can also be done manually by passing
   * `ButtonGroup` to `Dropdown`'s `as` prop.
   */
  split?: boolean;
}

/**
 * React-Bootstrap passes some Dropdown props on to the underlying DOM element,
 * triggering warnings that in turn cause tests to fail. This shim element
 * omits the offending props from being passed on.
 */
const DropdownShim = React.forwardRef<HTMLDivElement, DropdownProps>(
  ({ onSelect, split, ...props }, ref) => {
    const Component = split ? ButtonGroup : "div";
    return <Component {...props} ref={ref} />;
  },
);
DropdownShim.displayName = "DropdownShim";

/**
 * React-Bootstrap's `Dropdown` component, incorporating analytics and styled
 * to use the Along brand.
 *
 * This is a set of lower-level components that can be used to build customized
 * dropdown components, including controlling the toggle element, menu
 * positioning and alignment, etc.
 *
 * Docs: https://react-bootstrap.github.io/components/dropdowns/
 */
function Dropdown(props: DropdownProps) {
  return <RBDropdown {...props} as={props.as || DropdownShim} />;
}

Dropdown.Menu = $DropdownMenu;
Dropdown.Item = DropdownItem;
Dropdown.Toggle = DropdownToggle;

export default Dropdown;
